import React from 'react'
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormHelperText,
  Chip,
  Alert,
  InputAdornment,
  Tooltip,
} from '@mui/material'
import {
  HelpOutline as HelpIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material'
import { ServicePackageFormData } from '../../types/servicePackage'
import { getFieldError } from '../../utils/servicePackageValidation'

interface Section4SchedulingRulesProps {
  formData: ServicePackageFormData
  onUpdate: (data: Partial<ServicePackageFormData>) => void
  errors: any
}

export const Section4SchedulingRules: React.FC<
  Section4SchedulingRulesProps
> = ({ formData, onUpdate, errors }) => {
  const handleZipCodeChange = (zipCode: string) => {
    // Only allow adding if it's a valid 5-digit zip code
    if (/^\d{5}$/.test(zipCode)) {
      const updatedZipCodes = [
        ...formData.schedulingRules.inPersonSettings.zipCodes,
        zipCode,
      ]

      // Remove duplicates
      const uniqueZipCodes = [...new Set(updatedZipCodes)]

      onUpdate({
        schedulingRules: {
          ...formData.schedulingRules,
          inPersonSettings: {
            ...formData.schedulingRules.inPersonSettings,
            zipCodes: uniqueZipCodes,
          },
        },
      })

      return true
    }
    return false
  }

  const handleRemoveZipCode = (zipCode: string) => {
    const updatedZipCodes =
      formData.schedulingRules.inPersonSettings.zipCodes.filter(
        code => code !== zipCode
      )

    onUpdate({
      schedulingRules: {
        ...formData.schedulingRules,
        inPersonSettings: {
          ...formData.schedulingRules.inPersonSettings,
          zipCodes: updatedZipCodes,
        },
      },
    })
  }

  const sessionLengthError = getFieldError(errors, 'sessionLengthMin')
  const sessionBufferError = getFieldError(errors, 'session_buffer')
  const radiusError = getFieldError(errors, 'inPersonSettings.radiusMiles')

  const zipCodesError = getFieldError(errors, 'inPersonSettings.zipCodes')

  // Check if in-person or hybrid modality is selected
  const hasInPersonModality =
    formData.basics.modality.includes('in_person') ||
    formData.basics.modality.includes('hybrid')

  return (
    <Box id="scheduling-rules-root">
      <Typography variant="h6" gutterBottom>
        Scheduling & Rules
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure session length and service area for your therapy sessions.
      </Typography>

      {/* Session Parameters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Session Parameters
          </Typography>

          <Grid container spacing={3}>
            {/* Session Length */}
            <Grid item xs={6}>
              <TextField
                id="session-length-field"
                fullWidth
                label="Session Length (minutes)"
                type="number"
                value={formData.schedulingRules.sessionLengthMin}
                onChange={e =>
                  onUpdate({
                    schedulingRules: {
                      ...formData.schedulingRules,
                      sessionLengthMin: parseInt(e.target.value) || 0,
                    },
                  })
                }
                error={!!sessionLengthError}
                helperText={
                  sessionLengthError ||
                  'Session duration between 15-120 minutes'
                }
                inputProps={{ min: 15, max: 120 }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="session-buffer-field"
                fullWidth
                label="Session Buffer (minutes)"
                type="number"
                value={formData.schedulingRules.session_buffer || 0}
                onChange={e =>
                  onUpdate({
                    schedulingRules: {
                      ...formData.schedulingRules,
                      session_buffer: parseInt(e.target.value) || 0,
                    },
                  })
                }
                error={!!sessionBufferError}
                helperText={
                  sessionBufferError || 'Session buffer between 0-60 minutes'
                }
                inputProps={{ min: 0, max: 60 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Additional Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Fees & Cancellation Policies
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure fees and cancellation policies for your service package.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="Travel/Onsite Surcharge ($)"
                  type="number"
                  value={formData.schedulingRules.travelOnsiteSurcharge || ''}
                  onChange={e =>
                    onUpdate({
                      schedulingRules: {
                        ...formData.schedulingRules,
                        travelOnsiteSurcharge: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      },
                    })
                  }
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Additional fee charged for home visits or travel appointments. This surcharge covers travel time and expenses."
                          arrow
                          placement="top"
                        >
                          <HelpIcon
                            sx={{
                              fontSize: 18,
                              color: 'text.secondary',
                              cursor: 'help',
                            }}
                          />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="Cancellation Window (Hours)"
                  type="number"
                  value={formData.schedulingRules.cancellationWindowHours || ''}
                  onChange={e =>
                    onUpdate({
                      schedulingRules: {
                        ...formData.schedulingRules,
                        cancellationWindowHours: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      },
                    })
                  }
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ClockIcon
                          sx={{
                            fontSize: 20,
                            color: 'text.secondary',
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            hrs
                          </Typography>
                          <Tooltip title="The minimum number of hours before an appointment that patients must cancel to avoid cancellation fees. Cancellations made within this window may incur a late cancellation fee."
                            arrow
                            placement="top"
                          >
                            <HelpIcon
                              sx={{
                                fontSize: 18,
                                color: 'text.secondary',
                                cursor: 'help',
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="No-Show Fee ($)"
                  type="number"
                  value={formData.schedulingRules.noShowFee || ''}
                  onChange={e =>
                    onUpdate({
                      schedulingRules: {
                        ...formData.schedulingRules,
                        noShowFee: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      },
                    })
                  }
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Fee charged when a patient fails to attend their scheduled appointment without canceling. This helps compensate for the reserved time slot that could have been used for another patient."
                          arrow
                          placement="top"
                        >
                          <HelpIcon
                            sx={{
                              fontSize: 18,
                              color: 'text.secondary',
                              cursor: 'help',
                            }}
                          />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="Late Cancellation Fee ($)"
                  type="number"
                  value={formData.schedulingRules.lateCancellationFee || ''}
                  onChange={e =>
                    onUpdate({
                      schedulingRules: {
                        ...formData.schedulingRules,
                        lateCancellationFee: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      },
                    })
                  }
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Fee charged when a patient cancels their appointment within the cancellation window (less than the specified hours before the appointment). This is typically lower than the no-show fee."
                          arrow
                          placement="top"
                        >
                          <HelpIcon
                            sx={{
                              fontSize: 18,
                              color: 'text.secondary',
                              cursor: 'help',
                            }}
                          />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* In-Person Settings */}
      {hasInPersonModality && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              In-Person Service Area
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define the geographic area where you provide in-person services.
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={formData.schedulingRules.inPersonSettings.serviceArea}
                onChange={e => {
                  const newServiceArea = e.target.value as
                    | 'radius_miles'
                    | 'fixed_zip_list'
                  const currentServiceArea =
                    formData.schedulingRules.inPersonSettings.serviceArea

                  // Clear inputs when switching between options
                  const updates: any = {
                    serviceArea: newServiceArea,
                  }

                  // If switching to radius_miles, clear zip codes
                  if (newServiceArea === 'radius_miles') {
                    updates.zipCodes = []
                  }

                  // If switching to fixed_zip_list, clear radius
                  if (newServiceArea === 'fixed_zip_list') {
                    updates.radiusMiles = null
                  }

                  onUpdate({
                    schedulingRules: {
                      ...formData.schedulingRules,
                      inPersonSettings: {
                        ...formData.schedulingRules.inPersonSettings,
                        ...updates,
                      },
                    },
                  })
                }}
              >
                <FormControlLabel
                  value="radius_miles"
                  control={<Radio />}
                  label="Service Radius"
                />

                {formData.schedulingRules.inPersonSettings.serviceArea ===
                  'radius_miles' && (
                  <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                    <TextField
                      id="service-radius-field"
                      label="Radius"
                      type="number"
                      value={
                        formData.schedulingRules.inPersonSettings.radiusMiles ||
                        ''
                      }
                      onChange={e =>
                        onUpdate({
                          schedulingRules: {
                            ...formData.schedulingRules,
                            inPersonSettings: {
                              ...formData.schedulingRules.inPersonSettings,
                              radiusMiles: e.target.value
                                ? parseFloat(e.target.value)
                                : null,
                            },
                          },
                        })
                      }
                      error={!!radiusError}
                      helperText={
                        radiusError || 'Distance in miles (minimum 1)'
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">Miles</InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 1 }}
                      sx={{ width: 200 }}
                    />
                  </Box>
                )}

                <FormControlLabel
                  value="fixed_zip_list"
                  control={<Radio />}
                  label="Specific ZIP Codes"
                />

                {formData.schedulingRules.inPersonSettings.serviceArea ===
                  'fixed_zip_list' && (
                  <Box sx={{ ml: 4, mt: 1, mb: 2 }} id="service-zipcodes-field">
                    <TextField
                      label="Add ZIP Code"
                      placeholder="Enter 5-digit ZIP code"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          const input = e.target as HTMLInputElement
                          const zipCode = input.value.trim()
                          if (handleZipCodeChange(zipCode)) {
                            input.value = ''
                          }
                        }
                      }}
                      error={!!zipCodesError}
                      helperText={
                        zipCodesError ||
                        'Press Enter to add ZIP code (5 digits). Only one ZIP code at a time.'
                      }
                      sx={{ width: 200 }}
                    />

                    {formData.schedulingRules.inPersonSettings.zipCodes.length >
                      0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mt: 2,
                        }}
                      >
                        {formData.schedulingRules.inPersonSettings.zipCodes.map(
                          zipCode => (
                            <Chip
                              key={zipCode}
                              label={zipCode}
                              onDelete={() => handleRemoveZipCode(zipCode)}
                              color="primary"
                              variant="outlined"
                            />
                          )
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </RadioGroup>

              {!formData.schedulingRules.inPersonSettings.serviceArea && (
                <FormHelperText error>
                  Please select a service area type
                </FormHelperText>
              )}
            </FormControl>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Service Area:</strong> This information helps match you
                with patients in your geographic area. For radius-based service,
                the distance is calculated from your profile address.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default Section4SchedulingRules
